import { useState, useEffect, useRef } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"
import { ArrowLeft, Save } from "lucide-react"
import { toast } from 'sonner'
import { useUserRol, useTienePermiso} from '../App';
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService';
import { RechazoModal } from "./RechazoModal";

interface NMXA221FormData {
  // Información básica
  numeroSolicitud: string
  fecha: string
  realizo: string
  superviso: string
  
  // Mediciones
  masaInicial: string
  masaFinal: string
  porcentajeGrasa: string
  
  // Observaciones
  observaciones: string
  mostrarObservaciones: boolean
  // NUEVO: Detalles de cualquier variación
  detVariacion?: string
}

interface NMXA221FormProps {
  onBack: () => void
  pedidoId: string
  pedidoRowId?: string
  mode: 'create' | 'view' | 'edit'
  initialData?: Partial<NMXA221FormData>
  realizoUser?: string // NUEVO: nombre del usuario
  onSave?: (data: NMXA221FormData) => void // NUEVO: función de guardado
  rol?: string
  estatus?: string
  onRefreshBackend?: () => void
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

export function NMXA221Form({ onBack, pedidoId, pedidoRowId, mode, initialData, realizoUser, onSave, rol, estatus, onRefreshBackend, onAutosave }: NMXA221FormProps) {
  const globalRol = typeof useUserRol === 'function' ? useUserRol() : undefined;
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const effectiveRol = rol || globalRol;

  const [formData, setFormData] = useState<NMXA221FormData>({
    numeroSolicitud: "",
    fecha: "",
    realizo: "",
    superviso: "",
    masaInicial: "",
    masaFinal: "",
    porcentajeGrasa: "",
    observaciones: "",
    ...initialData,
    mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true,
  })

  const [editMode, setEditMode] = useState(false)
  const [estatusLocal, setEstatusLocal] = useState<string>(estatus || (initialData as any)?.ACEPTAR || '')

  // Lógica robusta para saber si la prueba está aceptada o rechazada
  const aceptadoRaw = (initialData as any)?.ACEPTADO ?? (initialData as any)?.aceptado ?? estatusLocal;
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : '';
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado';
  const isReadOnly = mode === 'view' && !editMode;

  // Auto-fill realizo in create mode
  useEffect(() => {
    if (mode === "create" && realizoUser) {
      setFormData(prev => ({ ...prev, realizo: realizoUser }))
    }
  }, [realizoUser, mode])

  // Sync with initialData in view/edit
  useEffect(() => {
    if (initialData) {
      if (mode === 'view') {
        setFormData({
          numeroSolicitud: "",
          fecha: "",
          realizo: "",
          superviso: "",
          masaInicial: "",
          masaFinal: "",
          porcentajeGrasa: "",
          observaciones: "",
          ...initialData,
          mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true,
        })
      } else {
        setFormData(prev => ({
          ...prev,
          ...initialData,
          mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true,
        }))
      }
    }
  }, [initialData, mode])

  useEffect(() => {
    if (mode === 'view' && effectiveRol !== 'ADMIN') {
      setEditMode(false)
    }
  }, [effectiveRol, mode])

  const handleInputChange = (field: keyof NMXA221FormData, value: any) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value }
      
      // Calcular automáticamente el porcentaje de grasa si se tienen masa inicial y final
      if ((field === 'masaInicial' || field === 'masaFinal') && 
          newData.masaInicial && newData.masaFinal) {
        const inicial = parseFloat(newData.masaInicial)
        const final = parseFloat(newData.masaFinal)
        if (!isNaN(inicial) && !isNaN(final) && inicial > 0) {
          const porcentaje = ((inicial - final) / inicial) * 100
          newData.porcentajeGrasa = porcentaje.toFixed(1)
        }
      }
      
      return newData
    })
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    if (typeof window !== 'undefined') {
      if (!formData.numeroSolicitud || !formData.fecha || !formData.realizo) {
        alert('Por favor completa los campos obligatorios: No. de Solicitud, Fecha y Realizó.');
        return;
      }
    }
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      if (typeof onSave === 'function') {
        onSave(formData);
      } else {
        console.log('Guardando NMX-A-221-1982:', formData);
        alert('Guardado simulado (no hay función de guardado real conectada).');
      }
    } finally {
      setGuardando(false)
    }
  }

  const [autosaveRowId, setAutosaveRowId] = useState<string>(
    (formData as any)?.ROW_ID || (initialData as any)?.ROW_ID || (initialData as any)?.row_id || ''
  )
  const [autosaveNomId, setAutosaveNomId] = useState<string>((initialData as any)?.nomId || '')
  const rowId = autosaveRowId;
  const nomId = autosaveNomId;
  const pruebaValue = (formData as any)?.PRUEBA || (initialData as any)?.PRUEBA || '';

  const formPayload = () => {
    const { ROW_ID, PRUEBA, PEDIDO, ...rest } = formData as any
    return rest
  }

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'NMX-A-221-1982 Determinación de las grasas y otros materiales solubles por extracción con cloruro de metileno',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onBack()
  }

  const handleAccion = async (accion: 'aceptado' | 'rechazado') => {
    const confirmMsg = accion === 'aceptado'
      ? 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba NMX-A-221?'
      : 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba NMX-A-221?';
    if (!window.confirm(confirmMsg)) return;
    const payload = {
      ...formData,
      ROW_ID: rowId,
      PEDIDO: pedidoId,
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      usuario: realizoUser,
      ACEPTAR: accion === 'aceptado' ? 'aceptado' : 'rechazado',
      ACEPTADO: accion
    };
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, accion, undefined, (payload as any).superviso, nomId);
      setEstatusLocal(payload.ACEPTAR);
      setEditMode(false);
      toast.success(`Prueba ${accion === 'aceptado' ? 'aceptada' : 'rechazada'} correctamente.`);
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err: any) {
      alert('Error al enviar al backend: ' + (err.message || err));
    }
  };

  const handleEditar = () => setEditMode(true)

  const handleGuardarEdicion = async () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de NMX-A-221? Esta acción no se puede deshacer.')) return;
    const payload = {
      ...formData,
      ROW_ID: rowId,
      PEDIDO: pedidoId,
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      usuario: realizoUser,
      ACEPTAR: 'aceptado',
      ACEPTADO: 'aceptado'
    };
    try {
      onSave({ ...payload, _EDIT_MODE: true } as any);
    } catch (err) {
      console.error('Error al guardar edición:', err);
    }
  }

  // Estados para el modal de rechazo
  const [rechazoModalOpen, setRechazoModalOpen] = useState(false);
  const [rechazoLoading, setRechazoLoading] = useState(false);

  // Handler para abrir el modal de rechazo
  const handleRechazar = () => setRechazoModalOpen(true);

  // Handler para enviar el rechazo con razón
  const handleRechazoSubmit = async (razon: string) => {
    setRechazoLoading(true);
    const confirmMsg = 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba NMX-A-221?';
    if (!window.confirm(confirmMsg)) {
      setRechazoLoading(false);
      setRechazoModalOpen(false);
      return;
    }
    const payload = {
      ...formData,
      ROW_ID: rowId,
      PEDIDO: pedidoId,
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      usuario: realizoUser,
      ACEPTAR: 'rechazado',
      ACEPTADO: 'rechazado',
      RAZON_RECHAZO: razon
    };
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, 'rechazado', razon, undefined, nomId);
      setEstatusLocal('rechazado');
      setEditMode(false);
      setRechazoModalOpen(false);
      toast.success('Prueba rechazada correctamente.');
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err: any) {
      alert('Error al enviar al backend: ' + (err.message || err));
    } finally {
      setRechazoLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      {/* Modal de rechazo */}
      <RechazoModal
        open={rechazoModalOpen}
        onClose={() => setRechazoModalOpen(false)}
        onSubmit={handleRechazoSubmit}
        loading={rechazoLoading}
      />
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={handleBackFlush}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">
                NMX-A-221-1982
              </h1>
              <p className="text-muted-foreground">
                Determinación de las grasas y otros materiales solubles por extracción con cloruro de metileno
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-4">
            <Button variant="outline" onClick={handleBackFlush}>
              Cancelar
            </Button>
            {/* Botones de acciones en modo view y no aceptado SOLO PARA ADMIN */}
            {mode === 'view' && !editMode && !isAceptado && puedeRevisar && (
              <>
                <Button variant="default" onClick={() => handleAccion('aceptado')}>Aceptar</Button>
                <Button variant="destructive" onClick={handleRechazar}>Rechazar</Button>
                <Button variant="secondary" onClick={handleEditar}>Editar</Button>
              </>
            )}
            {/* Botón guardar edición SOLO PARA ADMIN */}
            {mode === 'view' && editMode && !isAceptado && puedeRevisar && (
              <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
            )}
            {/* Botón guardar en modo create */}
            {mode === 'create' && (
              <Button onClick={handleSave} disabled={guardando}>
                <Save className="h-4 w-4 mr-2" />
                {guardando ? 'Guardando...' : 'Guardar Formulario'}
              </Button>
            )}
          </div>
        </div>

        {/* Información del Pedido */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-primary">Información del Pedido</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Pedido No: {pedidoId}</p>
          </CardContent>
        </Card>

        {/* Formulario Principal */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-primary">Formulario de Evaluación</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Primera tabla - Información básica */}
            <div className="overflow-x-auto">
              <table className="w-full border border-border text-sm">
                <tbody>
                  <tr>
                    {/* Primera fila - NO. DE SOLICITUD y FECHA */}
                    <td className="border border-border p-2 bg-muted font-medium w-1/4">
                      NO. DE SOLICITUD:
                    </td>
                    <td className="border border-border p-1 w-1/4">
                      <Input
                        type="text"
                        placeholder="NUMERO SOLICITUD"
                        value={formData.numeroSolicitud}
                        onChange={(e) => handleInputChange('numeroSolicitud', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent"
                      />
                    </td>
                    <td className="border border-border p-2 bg-muted font-medium w-1/4">
                      FECHA:
                    </td>
                    <td className="border border-border p-1 w-1/4">
                      <Input
                        type="date"
                        placeholder="<<FECHA>>"
                        value={formData.fecha}
                        onChange={(e) => handleInputChange('fecha', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Tabla principal con mediciones y personal */}
            <div className="mt-6 overflow-x-auto">
              <table className="w-full border border-border text-sm">
                <tbody>
                  <tr>
                    {/* Columna izquierda - Mediciones */}
                    <td className="border border-border p-0 w-2/3">
                      <table className="w-full h-full">
                        <tbody>
                          <tr>
                            <td className="border border-border p-3 bg-muted font-medium w-3/5">
                              Masa inicial
                            </td>
                            <td className="border border-border p-2 w-2/5">
                              <Input
                                type="number"
                                step="0.0001"
                                placeholder="MASA INICIAL"
                                value={formData.masaInicial}
                                onChange={(e) => handleInputChange('masaInicial', e.target.value)}
                                disabled={isReadOnly}
                                className="border-0 bg-transparent"
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="border border-border p-3 bg-muted font-medium">
                              Masa final
                            </td>
                            <td className="border border-border p-2">
                              <Input
                                type="number"
                                step="0.0001"
                                placeholder="MASA FINAL"
                                value={formData.masaFinal}
                                onChange={(e) => handleInputChange('masaFinal', e.target.value)}
                                disabled={isReadOnly}
                                className="border-0 bg-transparent"
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="border border-border p-3 bg-muted font-medium">
                              % grasa y otros materiales solubles
                            </td>
                            <td className="border border-border p-2">
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="GRASA"
                                value={formData.porcentajeGrasa}
                                onChange={(e) => handleInputChange('porcentajeGrasa', e.target.value)}
                                disabled={isReadOnly}
                                className="border-0 bg-transparent"
                              />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>

                    {/* Columna derecha - Personal */}
                    <td className="border border-border p-4 w-1/3 bg-gray-100 align-top">
                      <div className="space-y-4">
                        <div>
                          <label className="font-medium mb-2 block">REALIZÓ:</label>
                          <Input
                            type="text"
                            placeholder="REALIZÓ"
                            value={formData.realizo}
                            readOnly
                            className="bg-white cursor-not-allowed"
                          />
                        </div>
                        <div>
                          <label className="font-medium mb-2 block">SUPERVISÓ:</label>
                          <Input
                            type="text"
                            placeholder="SUPERVISÓ"
                            value={formData.superviso}
                            disabled
                            className="mt-2"
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Observaciones */}
        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-primary">Observaciones</CardTitle>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Mostrar en Reporte Cliente
              <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                onCheckedChange={(checked) => handleInputChange('mostrarObservaciones', checked)} />
            </span>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border border-border text-sm">
                <tbody>
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium w-1/6">
                      OBSERVACIONES:
                    </td>
                    <td className="border border-border p-1 w-5/6">
                      <Textarea
                        placeholder="<<OBSERVACIONES>>"
                        value={formData.observaciones}
                        onChange={(e) => handleInputChange('observaciones', e.target.value)}
                        disabled={isReadOnly}
                        className="min-h-[100px] border-0 bg-transparent resize-none"
                      />
                    </td>
                  </tr>
                  {/* NUEVO: Detalles de cualquier variación */}
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium w-1/6">
                      DETALLES DE CUALQUIER VARIACIÓN:
                    </td>
                    <td className="border border-border p-1 w-5/6">
                      <Textarea
                        placeholder="DETALLES DE VARIACIÓN"
                        value={formData.detVariacion}
                        onChange={(e) => handleInputChange('detVariacion', e.target.value)}
                        disabled={isReadOnly}
                        className="min-h-[100px] border-0 bg-transparent resize-none"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}